<script setup lang="ts">
  import { computed, h, ref, watch } from 'vue'
  import { Button } from '@/components/ui/button'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { MoreVertical } from 'lucide-vue-next'
  import { MegaphoneIcon } from '@heroicons/vue/24/outline'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore.ts'
  import { caseTypesDE } from '@/i18n/caseTypes'
  import router from '@/router'
  import {
    StateEnum,
    type CustomerCase as Case,
    CasesService,
    DuplicateCaseMatchesService,
    FlagEnum,
    RelationshipTypeEnum,
    EntityTypeEnum
  } from '@/types/generated'
  import LegacyIntegrationButtons from '@/components/app/cases/legacy/LegacyIntegrationButtons.vue'
  import { ROUTES } from '@/router/routes.ts'
  import { useToast } from '@/components/ui/toast'
  import { useMetrics } from '@/composables/useMetrics'
  import { EventNameEnum } from '@/types/generated'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'

  const { t, locale } = useTranslation({
    en: {
      aiChat: 'AI Chat',
      cancel: 'Cancel',
      completed: 'COMPLETED',
      completeRequest: 'Complete Request',
      createdOn: 'Created on',
      deleteRequest: 'Delete Request',
      deleteRequestConfirmation: 'Are you sure you want to delete this request?',
      deleteRequestWarning:
        'Are you sure you want to delete this request? This action cannot be undone.',
      deleteRequestError: 'Failed to delete the case. Please try again.',
      delete: 'Delete',
      downloadAllDocuments: 'Download All Documents',
      downloadError: 'An error occurred while downloading the documents.',
      entities: 'Entities',
      error: 'Error',
      general: 'General',
      inProgress: 'IN PROGRESS',
      lawSpecificData: 'Law Specific Data',
      new: 'NEW',
      noDocuments: 'No Documents',
      noDocumentsForDownload: 'No documents available for download.',
      rejectRequest: 'Reject Request',
      rejected: 'REJECTED',
      reopenRequest: 'Set Request to "Open"',
      requestFrom: 'Request from',
      requestInProgress: 'Request in Progress',
      statusUpdateError: 'Status could not be updated.',
      success: 'Success',
      duplicatesDismissed: 'All duplicate suggestions have been dismissed.',
      duplicatesDismissError: 'Error dismissing duplicate suggestions.',
      documents: 'Documents',
      duplicateCaseMessage:
        'This case appears to be a duplicate, you can merge it with the existing case.',
      checkExistingCases: 'Check the existing cases',
      keepThisCase: 'Keep this case'
    },
    de: {
      aiChat: 'KI-Chat',
      cancel: 'Abbrechen',
      completed: 'ABGESCHLOSSEN',
      completeRequest: 'Anfrage abschließen',
      createdOn: 'Erstellt am',
      deleteRequest: 'Anfrage löschen',
      deleteRequestConfirmation: 'Sind Sie sicher, dass Sie diese Anfrage löschen möchten?',
      deleteRequestWarning: 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
      deleteRequestError:
        'Die Anfrage konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
      delete: 'Löschen',
      downloadAllDocuments: 'Alle Dokumente herunterladen',
      downloadError: 'Beim Herunterladen der Dokumente ist ein Fehler aufgetreten.',
      entities: 'Beteiligte',
      error: 'Fehler',
      general: 'Allgemein',
      inProgress: 'IN BEARBEITUNG',
      lawSpecificData: 'Rechtsspezifische Daten',
      new: 'NEU',
      noDocuments: 'Keine Dokumente',
      noDocumentsForDownload: 'Keine Dokumente zum Herunterladen verfügbar.',
      rejectRequest: 'Anfrage ablehnen',
      rejected: 'ABGELEHNT',
      reopenRequest: 'Anfrage auf "offen" setzen',
      requestFrom: 'Anfrage von',
      requestInProgress: 'Anfrage in Bearbeitung',
      statusUpdateError: 'Status konnte nicht aktualisiert werden.',
      success: 'Erfolg',
      duplicatesDismissed: 'Alle Duplikat-Vorschläge wurden abgelehnt.',
      duplicatesDismissError: 'Fehler beim Ablehnen der Duplikat-Vorschläge.',
      documents: 'Dokumente',
      duplicateCaseMessage:
        'Diese Anfrage scheint ein Duplikat zu sein. Sie können sie mit der bestehenden Anfrage zusammenführen.',
      checkExistingCases: 'Bestehende Anfragen prüfen',
      keepThisCase: 'Diese Anfrage behalten'
    },
    fr: {
      aiChat: 'Chat IA',
      cancel: 'Annuler',
      completed: 'TERMINÉ',
      completeRequest: 'Clôturer la demande',
      createdOn: 'Créé le',
      deleteRequest: 'Supprimer la demande',
      deleteRequestConfirmation: 'Êtes-vous sûr de vouloir supprimer cette demande ?',
      deleteRequestWarning: 'Cette action est irréversible.',
      deleteRequestError: "La demande n'a pas pu être supprimée. Veuillez réessayer.",
      delete: 'Supprimer',
      downloadAllDocuments: 'Télécharger tous les documents',
      downloadError: 'Une erreur est survenue lors du téléchargement des documents.',
      entities: 'Entités',
      error: 'Erreur',
      general: 'Général',
      inProgress: 'EN COURS',
      lawSpecificData: 'Données juridiques spécifiques',
      new: 'NOUVEAU',
      noDocuments: 'Aucun document',
      noDocumentsForDownload: 'Aucun document disponible au téléchargement.',
      rejectRequest: 'Refuser la demande',
      rejected: 'REFUSÉ',
      reopenRequest: 'Remettre la demande en "ouverte"',
      requestFrom: 'Demande de',
      requestInProgress: 'Demande en cours',
      statusUpdateError: "Le statut n'a pas pu être mis à jour.",
      success: 'Succès',
      duplicatesDismissed: 'Toutes les suggestions de doublons ont été rejetées.',
      duplicatesDismissError: 'Erreur lors du rejet des suggestions de doublons.',
      documents: 'Documents',
      duplicateCaseMessage:
        'Cette demande semble être un doublon. Vous pouvez la fusionner avec la demande existante.',
      checkExistingCases: 'Vérifier les demandes existantes',
      keepThisCase: 'Conserver cette demande'
    }
  })

  const caseStore = useCaseStore()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()
  const caseDuplicatePreventionEnabled = useFeatureFlag(FlagEnum.CASE_DUPLICATE_PREVENTION)

  // Check feature flags immediately to avoid layout shift
  caseDuplicatePreventionEnabled.checkFlag()

  const isUpdatingCaseStatus = ref(false)
  const isDeleteDialogOpen = ref(false)
  const isDismissingDuplicates = ref(false)

  // Local state for immediate UI updates
  const localCaseStatus = ref<StateEnum | null>(null)

  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const entities = computed(() => caseRecord.value?.data?.entities)

  // Count entities with proposed changes
  const entitiesWithProposedChanges = computed(() => {
    if (!entities.value) return 0
    return entities.value.filter(
      (entity) => entity.entity?.proposed_changes && entity.entity.proposed_changes.length > 0
    ).length
  })

  // Reset local state when case record changes
  watch(
    () => caseRecord.value?.state,
    (newState) => {
      // Only reset local state if we're not in the middle of an update
      if (!isUpdatingCaseStatus.value) {
        localCaseStatus.value = null
      }
    }
  )

  const caseClientName = computed(() => {
    if (!caseRecord.value?.data?.entities) return ''

    const clientEntity = caseRecord.value.data.entities.find((entity) =>
      entity.relationships.some(
        (rel) => rel.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    )

    if (!clientEntity?.entity) return ''

    const entity = clientEntity.entity

    let entityName = ''
    if (entity.last_name) {
      if (entity.first_name && entity.first_name.length > 0) {
        const firstNameChar = entity.first_name.charAt(0).toUpperCase()
        entityName = `${firstNameChar}. ${entity.last_name}`.trim()
      } else {
        entityName = entity.last_name
      }
    } else if (entity.first_name) {
      entityName = entity.first_name
    } else {
      entityName = ''
    }

    switch (entity.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON: {
        return entityName
      }
      case EntityTypeEnum.LEGAL_PERSON:
        return entity.company_name || entityName || ''
      case EntityTypeEnum.INSTITUTION:
        return entity.institution_name || entityName || ''
      default:
        return entityName || ''
    }
  })

  const translatedCaseType = computed(() => {
    if (!caseRecord.value?.type) return ''
    return caseTypesDE[caseRecord.value.type as keyof typeof caseTypesDE] || caseRecord.value.type
  })

  const localeMap: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR'
  }

  const formattedCreationDate = computed(() => {
    if (!caseRecord.value?.created_at) return ''
    const date = new Date(caseRecord.value.created_at)
    const dateLocale = localeMap[locale.value] || 'en-US'
    const formatted = date.toLocaleString(dateLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

    if (locale.value === 'de') {
      return formatted.replace(',', ' um') + ' Uhr'
    }

    return formatted
  })

  const caseStatus = computed(() => {
    // Use local state if available, otherwise use the case record state
    const currentState = localCaseStatus.value || caseRecord.value?.state
    if (!currentState) return 'new'

    switch (currentState) {
      case StateEnum.COMPLETED:
        return 'completed'
      case StateEnum.IN_PROGRESS:
        return 'inProgress'
      case StateEnum.REJECTED:
        return 'rejected'
      default:
        return 'new'
    }
  })

  const statusBadgeClass = computed(() => {
    switch (caseStatus.value) {
      case 'completed':
        return 'bg-green-600'
      case 'inProgress':
        return 'bg-[#17a2b8]'
      case 'rejected':
        return 'bg-red-600'
      default:
        return 'bg-black'
    }
  })

  const pages = computed(() => {
    const generalRoute = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(
      ':uuid',
      caseRecord.value?.uuid ?? ''
    )

    const enableLawSpecificData = caseRecord.value?.org?.enable_law_specific_data ?? false

    const tabs = [
      {
        title: () => h('span', { 'data-cy': 'tab-allgemein-text' }, t('general')),
        route: generalRoute
      }
    ]

    if (enableLawSpecificData) {
      tabs.push(
        {
          title: () => h('span', t('lawSpecificData')),
          route: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(
            ':uuid',
            caseRecord.value?.uuid ?? ''
          )
        },
        {
          title: () => {
            return h('span', { class: 'flex gap-2 items-center' }, [
              t('entities'),
              entitiesWithProposedChanges.value > 0
                ? h(
                    'span',
                    {
                      class:
                        'flex h-4 w-4 text-[10px] items-center justify-center rounded-full bg-purple-600 font-semibold text-white'
                    },
                    `${entitiesWithProposedChanges.value}`
                  )
                : null
            ])
          },
          route: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(
            ':uuid',
            caseRecord.value?.uuid ?? ''
          ).replace('/:entityUuid?', '')
        }
      )
    }

    tabs.push({
      title: () => h('span', t('documents')),
      route: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENTS.replace(':uuid', caseRecord.value?.uuid ?? '')
    })

    if (enableLawSpecificData) {
      tabs.push({
        title: () => h('span', t('aiChat')),
        route: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT.replace(':uuid', caseRecord.value?.uuid ?? '')
      })
    }

    return tabs
  })

  const activePage = computed(() => {
    const currentPath = router.currentRoute.value.fullPath

    return pages.value.find((page) => {
      // For entities page, match both with and without entityUuid
      if (page.route.includes('entities')) {
        return currentPath.includes('entities')
      }
      // For law-specific-data page
      if (page.route.includes('law-specific-data')) {
        return currentPath.includes('law-specific-data')
      }
      // For chat page
      if (page.route.includes('chat')) {
        return currentPath.includes('chat')
      }
      // For general page
      return page.route === currentPath.replace(/\/+$/, '')
    })
  })

  const isCaseCompleted = computed(() => {
    // Use local state if available, otherwise use the case record state
    const currentState = localCaseStatus.value || caseRecord.value?.state
    return currentState === StateEnum.COMPLETED
  })

  const updateCaseStatus = async (status: StateEnum) => {
    if (!caseRecord.value?.uuid) return

    try {
      isUpdatingCaseStatus.value = true

      // Update local state immediately for responsive UI
      localCaseStatus.value = status

      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          state: status
        }
      })

      // Reload the case to get the updated state
      await caseStore.loadCase(caseRecord.value.uuid)

      // Clear local state after successful update
      localCaseStatus.value = null
    } catch (error) {
      console.error(`Error updating case status to ${status}:`, error)
      // Revert local state on error
      localCaseStatus.value = caseRecord.value?.state || null

      // Show error toast
      toast({
        title: t('error'),
        description: t('statusUpdateError'),
        variant: 'destructive'
      })
    } finally {
      isUpdatingCaseStatus.value = false
    }
  }

  const completeCase = () => updateCaseStatus(StateEnum.COMPLETED)
  const reopenCase = () => updateCaseStatus(StateEnum.NEW)
  const rejectRequest = () => updateCaseStatus(StateEnum.REJECTED)
  const markRequestInProgress = () => updateCaseStatus(StateEnum.IN_PROGRESS)
  const deleteRequest = async () => {
    if (!caseRecord.value?.uuid) return

    try {
      await CasesService.casesDestroy({
        uuid: caseRecord.value.uuid
      })
      isDeleteDialogOpen.value = false
      // Navigate to case overview after successful deletion. Use window.location for full page reload to Django route
      window.location.href = '/cases/'
    } catch (error) {
      console.error('Error deleting case:', error)
      toast({
        title: t('error'),
        description: t('deleteRequestError'),
        variant: 'destructive'
      })
    }
  }

  const downloadAllDocuments = async () => {
    if (!caseRecord.value?.uuid) return

    try {
      const response = await CasesService.casesDownloadDocumentsZipRetrieve({
        uuid: caseRecord.value.uuid
      })

      // Create a blob from the response and trigger download
      const blob = new Blob([response as unknown as Blob], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `jupus_export_${caseRecord.value.data?.client?.last_name || 'case'}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading documents:', error)
      // Check if it's a 404 error (no documents)
      if ((error as any).status === 404) {
        toast({
          title: t('noDocuments'),
          description: t('noDocumentsForDownload'),
          variant: 'destructive'
        })
      } else {
        toast({
          title: t('error'),
          description: t('downloadError'),
          variant: 'destructive'
        })
      }
    }
  }

  const getTabId = (route: string) => {
    if (route.includes('entities')) return 'entities'
    if (route.includes('law-specific-data')) return 'law-specific-data'
    if (route.includes('chat')) return 'ai-chat'
    return 'allgemein'
  }

  const handleNavigation = (route: string) => {
    // We need to handle navigation differently depending on the destination:
    if (route.startsWith('/cases/')) {
      // OLD FRONTEND NAVIGATION (/cases/:id)
      // Use window.location.href because:
      // 1. The old frontend is a separate Vue app with its own router
      // 2. Routes like /cases/:id don't exist in this SPA's route configuration
      // 3. Using router.push() would cause Vue Router warnings and fail to navigate
      // 4. window.location.href triggers a full page reload to load the old frontend
      window.location.href = route
    } else {
      router.push(route)
    }
  }

  const handleCheckExistingCases = () => {
    if (!caseRecord.value?.uuid) return
    trackEvent(EventNameEnum.DUPLICATE_CASE_BANNER_CHECK_CASES_CLICKED, {
      case_uuid: caseRecord.value.uuid
    })
    router.push(`/app/cases/${caseRecord.value.uuid}/merge-into`)
  }

  const handleKeepThisCase = async () => {
    if (!caseRecord.value?.uuid || isDismissingDuplicates.value) return

    trackEvent(EventNameEnum.DUPLICATE_CASE_BANNER_KEEP_CASE_CLICKED, {
      case_uuid: caseRecord.value.uuid
    })

    try {
      isDismissingDuplicates.value = true

      await DuplicateCaseMatchesService.duplicateCaseMatchesForCaseDismissCreate({
        caseUuid: caseRecord.value.uuid,
        requestBody: {}
      })

      // Reload the case to update has_pending_duplicate_matches flag
      await caseStore.loadCase(caseRecord.value.uuid)

      toast({
        title: t('success'),
        description: t('duplicatesDismissed'),
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('duplicatesDismissError'),
        variant: 'destructive'
      })
    } finally {
      isDismissingDuplicates.value = false
    }
  }

  const showDuplicateBanner = computed(() => {
    return (
      caseDuplicatePreventionEnabled.isEnabled.value &&
      caseRecord.value?.has_pending_duplicate_matches
    )
  })
</script>

<template>
  <div>
    <!-- Duplicate Case Banner -->
    <div
      v-if="showDuplicateBanner"
      class="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-4 w-screen border-b border-purple-200
        bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-2"
    >
      <div class="mx-auto flex max-w-7xl items-center gap-4">
        <div
          class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-purple-200"
        >
          <MegaphoneIcon class="h-5 w-5 text-neutral-800" />
        </div>
        <p class="flex-1 text-sm text-gray-800">
          {{ t('duplicateCaseMessage') }}
        </p>
        <div class="flex gap-2">
          <Button
            class="h-8 bg-purple-600 text-white hover:bg-purple-700"
            @click="handleCheckExistingCases"
          >
            {{ t('checkExistingCases') }}
          </Button>
          <Button
            variant="secondary"
            class="h-8 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            :disabled="isDismissingDuplicates"
            @click="handleKeepThisCase"
          >
            {{ isDismissingDuplicates ? '...' : t('keepThisCase') }}
          </Button>
        </div>
      </div>
    </div>

    <div class="flex flex-row flex-wrap items-start justify-between gap-4 pt-4 md:flex-nowrap">
      <div class="ml-0 mt-2 h-auto min-w-0 grow">
        <div
          id="title"
          class="position-relative align-items-center justify-content-start flex min-w-0 gap-3"
        >
          <h1
            class="text-h1 font-weight-bold min-w-0 truncate"
            :title="caseRecord?.case_title || caseClientName"
          >
            {{ t('requestFrom') }} {{ caseRecord?.case_title || caseClientName }}
          </h1>
          <span
            class="badge shrink-0"
            :class="statusBadgeClass"
          >
            {{ t(caseStatus) }}
          </span>
        </div>
        <p class="text-sm text-stone-500">
          {{ translatedCaseType }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true"
            data-slot="icon"
            class="inline size-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
            ></path>
          </svg>
          {{ t('createdOn') }} {{ formattedCreationDate }}
        </p>
      </div>
      <div
        id="options"
        class="my-3 flex h-full shrink-0 items-start justify-end gap-2"
      >
        <LegacyIntegrationButtons :case-record="caseRecord" />
        <Button
          v-if="!isCaseCompleted"
          variant="default"
          class="h-9 bg-[#0ca678] text-white hover:bg-[#0a8e67]"
          :disabled="isUpdatingCaseStatus"
          @click="completeCase"
        >
          {{ isUpdatingCaseStatus ? '...' : t('completeRequest') }}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              class="h-9 min-w-10 border border-gray-300 bg-white"
            >
              <MoreVertical class="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              v-if="(localCaseStatus || caseRecord?.state) !== StateEnum.NEW"
              @click="reopenCase"
            >
              {{ t('reopenRequest') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              v-if="(localCaseStatus || caseRecord?.state) !== StateEnum.REJECTED"
              @click="rejectRequest"
            >
              {{ t('rejectRequest') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              v-if="(localCaseStatus || caseRecord?.state) !== StateEnum.IN_PROGRESS"
              @click="markRequestInProgress"
            >
              {{ t('requestInProgress') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              class="text-red-500 focus:bg-red-50 focus:text-red-600"
              @click="isDeleteDialogOpen = true"
            >
              {{ t('deleteRequest') }}
            </DropdownMenuItem>
            <DropdownMenuItem @click="downloadAllDocuments">
              {{ t('downloadAllDocuments') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <AlertDialog v-model:open="isDeleteDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('deleteRequestConfirmation') }}</AlertDialogTitle>
          <AlertDialogDescription>{{ t('deleteRequestWarning') }}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="isDeleteDialogOpen = false">
            {{ t('cancel') }}
          </AlertDialogCancel>
          <AlertDialogAction @click="deleteRequest">{{ t('delete') }}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- Navigation section - full width below header -->
    <div class="w-full border-b border-gray-200 pb-0">
      <nav class="flex w-full flex-wrap gap-x-1 overflow-hidden pt-1">
        <button
          v-for="(page, index) in pages"
          :key="page.route"
          :data-tab="getTabId(page.route)"
          :data-cy="`tab-${getTabId(page.route)}`"
          class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 py-2 text-sm
            hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50 md:gap-x-2 md:text-sm"
          :class="[
            page.route === activePage?.route
              ? 'border-purple-600 font-semibold text-purple-600'
              : 'border-transparent',
            index === 0 ? 'pr-1' : 'px-1'
          ]"
          @click="handleNavigation(page.route)"
        >
          <component :is="page.title" />
        </button>
      </nav>
    </div>
  </div>
</template>
